// Exchanges v2 — the old screen's rules, copied whole (screens/Exchanges.tsx keeps its own until the old screens go).
// Nothing here is new: the response shapes of GET /api/cex and /api/cex/movements, the error-kind words, the Move money
// door from a movement, the fee words. The CEX lane owns the logic; the rebuild only moved it out of the component.
import { fmtQuantity, displayAsset, isoToDubaiWall } from '../../../lib/format';
import { sameAsset } from '../../../lib/naming';

/** How far back Fills reads (the old screen's FILLS_DAYS). */
export const FILLS_DAYS = 30;

export const ENTITY_LABEL: Record<string, string> = { dubaiotc: 'DubaiOTC', indiaotc: 'IndiaOTC', euotc: 'EuOTC' };

/** CCXT's failure kind (scripts/cex-sync.mjs classify()) as a word, and — for a key that needs a human — what to do. */
export const ERROR_KIND: Record<string, { label: string; fix: string }> = {
  permanent:      { label: 'needs attention',  fix: '' },
  auth:           { label: 'key rejected',     fix: 'check the API key/secret (and passphrase) in the keychain' },
  permission:     { label: 'key lacks access', fix: 'the key needs read permission for this wallet' },
  unsupported:    { label: 'not supported',    fix: 'this venue or wallet type cannot be read this way' },
  rate_limit:     { label: 'rate limited',     fix: 'transient — the next sync should succeed' },
  timeout:        { label: 'timed out',        fix: 'transient — the next sync should succeed' },
  venue_down:     { label: 'venue unavailable',fix: 'transient — the exchange is not answering' },
  maintenance:    { label: 'in maintenance',   fix: 'transient — the exchange is in maintenance' },
  clock_skew:     { label: 'clock skew',       fix: "this Mac's clock has drifted from the venue's" },
  network:        { label: 'network error',    fix: 'transient — check this machine is online' },
  exchange_error: { label: 'exchange error',   fix: 'the venue rejected the request' },
  unknown:        { label: 'unknown error',    fix: '' },
};

// ─── Shapes returned by GET /api/cex (mirror the view columns) ─────────────
export interface RollupRow {
  entity: string; asset: string;
  total: string; free: string; used: string;
  accounts: number; stale_accounts: number; unreachable_accounts: number;
  oldest_observation: string | null;
}
export interface PositionRow {
  account_id: number; entity: string; venue: string; label: string; status: string;
  wallet: string; asset: string; free: string; used: string; total: string;
  as_of: string; stale: boolean; last_run_ok: boolean | null;
  last_run_error: string | null; last_run_error_kind: string | null;
  last_run_error_permanent: boolean | null; last_run_at: string | null;
}
export interface AccountRow {
  account_id: number; entity: string; venue: string; label: string; status: string;
  last_run_ok: boolean | null; last_run_error: string | null;
  last_run_error_kind: string | null; last_run_error_permanent: boolean | null;
  last_run_at: string | null;
}
export interface CexResponse { rollup: RollupRow[]; positions: PositionRow[]; accounts: AccountRow[]; entities: string[] }

export interface MovementRow {
  /** The books rail for this movement's account, resolved by the BFF (25 Sept 2026). */
  venue_rail?: string | null;
  id: number; entity: string; venue: string; label: string; kind: string; asset: string;
  amount: string; direction: string; fee_amount: string | null; fee_asset: string | null;
  status: string | null; network: string | null; tx_hash: string | null; occurred_at: string;
  /** The chain row this movement is (bff/src/routes/chain.ts venueLinkSql); null when the chain read saw nothing. */
  chain_transfer_id?: number | null; wallet_slug?: string | null; chain_amount?: string | null;
  block_time?: string | null; chain_tx_hash?: string | null;
}

/** THE MOVE MONEY DOOR FROM A MOVEMENT (16 Sept 2026) — the old screen's moveMoneyUrl, verbatim: a deposit is wallet →
 *  venue, a withdrawal venue → wallet; the chain's amount, the venue's fee when in the same coin, the block's time, the
 *  chain hash in the note. Null when the movement meets no chain row. */
export function moveMoneyUrl(m: MovementRow): string | null {
  if (m.chain_transfer_id == null || !m.wallet_slug || !m.chain_amount || !m.block_time) return null;
  const venueSlug = m.venue_rail
    ?? `exchange.${m.venue}${m.entity === 'indiaotc' ? '-india' : ''}`;
  const deposit = m.kind === 'deposit';
  const fee = !deposit && m.fee_amount && sameAsset(m.fee_asset, m.asset) && Math.abs(Number(m.fee_amount)) > 0
    ? Math.abs(Number(m.fee_amount)) : 0;
  const q = new URLSearchParams({
    rail: deposit ? m.wallet_slug : venueSlug, railAsset: m.asset,
    to:   deposit ? venueSlug : m.wallet_slug, toAsset: m.asset,
    amount: String(Number(m.chain_amount)),   // the BFF's numeric string, without its trailing zeros
    when: isoToDubaiWall(m.block_time),
    note: `chain ${(m.chain_tx_hash ?? '').slice(0, 14)}`,
  });
  if (fee > 0) q.set('fee', String(fee));
  return `/treasury/rebalance?${q.toString()}`;
}

/** "0.00207 BTC + 249.77 USDC", or an em dash when the venue kept nothing. */
export function feeWords(fees: Record<string, number>): string {
  const parts = Object.entries(fees).filter(([, v]) => v !== 0).map(([a, v]) => `${fmtQuantity(v, a)} ${displayAsset(a)}`);
  return parts.length ? parts.join(' + ') : '—';
}
